import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useSuche, useTrip } from '../../hooks/queries'
import type { SearchHit } from '../../api/types'
import { fehlertext } from '../../api/client'
import { zeitText } from '../../lib/ortszeit'
import { BackIcon } from '../../components/Icons'
import { Netzstreifen } from '../../components/Netzstreifen'
import { useTexte } from '../../texte'

/**
 * Searching a trip's thread (1.17.0).
 *
 * A category of the trip's info page that leaves for this page: one field,
 * a list of hits, and a tap lands in the chat at the message (`?m=`, the
 * same way a notification lands there -- and since 1.17.0 that way loads as
 * many pages as it takes). For the traveller a hit may live in a reader's
 * thread; the server says which one (`with`), and the tap goes there.
 *
 * **The server decides what is found.** Every hit went through `Visibility`;
 * this page shows what it gets and asks nothing of its own. The word is sent
 * only from two characters on -- asking per keystroke for nothing would be a
 * request per keystroke.
 */
export function SuchePage() {
  const t = useTexte()
  const navigate = useNavigate()
  const { tripId: roh } = useParams()
  const tripId = Number(roh)
  const trip = useTrip(tripId)
  const [wort, setWort] = useState('')
  const suche = useSuche(tripId, wort)
  const versatz = trip.data?.tz_offset_min ?? null

  function oeffnen(treffer: SearchHit) {
    const chat = treffer.with === null ? `/t/${tripId}` : `/t/${tripId}/with/${treffer.with}`
    navigate(`${chat}?m=${treffer.id}`)
  }

  const treffer = suche.data?.hits ?? []
  const sucht = wort.trim().length >= 2 && suche.isFetching && suche.data === undefined

  return (
    <div className="suche" data-suche="">
      <Netzstreifen />
      <header className="bar">
        <Link className="icon-button" to={`/t/${tripId}/info`} aria-label={t.suche.zurueck}>
          <BackIcon />
        </Link>
        <h2 className="bar__title bar__grow">{t.suche.titel}</h2>
      </header>
      <div className="suche__inhalt">
        <input
          type="search"
          className="field__input suche__feld"
          value={wort}
          onChange={(e) => setWort(e.target.value)}
          placeholder={t.suche.feld}
          aria-label={t.suche.feld}
          autoComplete="off"
          enterKeyHint="search"
          data-suche-feld=""
        />
        <p className="leise leise--absatz">{t.suche.hinweis}</p>

        {suche.isError && (
          <p className="notice" role="alert">
            {fehlertext(suche.error)}
          </p>
        )}
        {sucht && <p className="leise">{t.suche.sucht}</p>}
        {suche.data !== undefined && wort.trim().length >= 2 && (
          <>
            <p className="leise" data-suche-anzahl={treffer.length}>
              {treffer.length === 0 ? t.suche.keineTreffer : t.suche.treffer(treffer.length)}
            </p>
            {suche.data.more && <p className="leise">{t.suche.mehr}</p>}
            <ul className="suche__liste">
              {treffer.map((h) => (
                <li key={h.id}>
                  <button
                    type="button"
                    className="suche__treffer"
                    onClick={() => oeffnen(h)}
                    title={t.suche.oeffnen}
                    data-suche-treffer={h.id}
                  >
                    <span className="suche__kopf">
                      <span>{h.author.display_name}</span>
                      <span>{zeitText(h.created_at, versatz)}</span>
                    </span>
                    <p className="suche__text">{hervorgehoben(h.excerpt, wort.trim())}</p>
                    {h.media_count > 0 && (
                      <span className="leise leise--klein">{t.suche.medien(h.media_count)}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}

/** The word marked inside the excerpt, case-insensitively, every time it occurs. */
function hervorgehoben(text: string, wort: string): ReactNode {
  if (wort === '') return text
  const teile: ReactNode[] = []
  const klein = text.toLocaleLowerCase()
  const gesucht = wort.toLocaleLowerCase()
  let von = 0
  for (;;) {
    const bei = klein.indexOf(gesucht, von)
    if (bei === -1) break
    if (bei > von) teile.push(text.slice(von, bei))
    teile.push(<mark key={bei}>{text.slice(bei, bei + wort.length)}</mark>)
    von = bei + wort.length
  }
  if (von < text.length) teile.push(text.slice(von))
  return teile
}
